import { Form, FormProps, Input, Space, Spin } from 'antd';
import { ComponentRef, forwardRef, ForwardRefRenderFunction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../buttons/custom-button/custom-button';
import styles from './single-input-form.module.css';

interface FormModel {
  value: string;
}

interface Props extends FormProps<FormModel> {
  form: NonNullable<FormProps<FormModel>['form']>;
  isSubmitting: boolean;
  label: string;
  onCancel: () => void;
}

const SingleInputForm: ForwardRefRenderFunction<ComponentRef<typeof Form<FormModel>>, Props> = (
  { form, isSubmitting, onCancel, label, initialValues, onFinish, ...formProps },
  ref,
) => {
  const { t } = useTranslation('common');

  const handleFinish = (values: FormModel) => {
    onFinish?.(values);
  };

  useEffect(() => {
    form.resetFields();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Form
      ref={ref}
      layout="vertical"
      form={form}
      initialValues={initialValues}
      onFinish={handleFinish}
      requiredMark={false}
      {...formProps}
    >
      <Spin tip={t('hint.loading')} spinning={isSubmitting ?? false} size="small">
        <Form.Item
          name="value"
          label={label}
          className={styles.formItem}
          rules={[{ required: true, message: t('errors.required') }]}
        >
          <Input />
        </Form.Item>
        <Space className={styles.actionButtonsWrapper}>
          <CustomButton ghost type="primaryLight" htmlType="button" onClick={onCancel} size="small" fontSize={12}>
            {t('action.cancel')}
          </CustomButton>
          <CustomButton type="primary" htmlType="submit" size="small" fontSize={12}>
            {t('action.save')}
          </CustomButton>
        </Space>
      </Spin>
    </Form>
  );
};

export default forwardRef(SingleInputForm);
